import { TTSOptions, TextToSpeech } from "@capacitor-community/text-to-speech";
import { useCallback, useState } from "react";
import { useRecoilValueLoadable } from "recoil";
import { accessibilityAtom } from "@state/State";
import { IAccessibilitySettings } from "@interfaces/Interfaces";

const isImageTag = (element: Element): boolean => {
  return element.tagName === "IMG" || element.tagName === "ION-IMG";
};

const extractTextWithAltText = (element: Element | null): string => {
  if (!element) return "";

  let result = "";

  for (const node of Array.from(element.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      result += `${node.textContent ?? ""}\n`;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;

      if (isImageTag(el)) {
        const alt = el.getAttribute("alt");
        if (alt) result += `${alt}\n`;
      } else if (el.tagName === "ION-TOGGLE") {
        console.log(el);
        const checked = el.getAttribute("checked");
        if (checked !== null) result += `${checked === "true" ? "Enabled" : "Disabled"}\n`;
      } else {
        result += extractTextWithAltText(el);
      }
    }
  }

  return result;
};

export const useTextToSpeech = (textContainerId: string) => {
  const [playing, setPlaying] = useState<boolean>(false);
  const accessibilitySettings = useRecoilValueLoadable<IAccessibilitySettings | null>(accessibilityAtom);

  const handleAudioPlayback = useCallback(async (): Promise<void> => {
    try {
      if (playing) {
        setPlaying(false);
        TextToSpeech.stop();
      } else {
        const content = document.getElementById(textContainerId);
        let text: string = extractTextWithAltText(content);
        const supportedLanguages = await TextToSpeech.getSupportedLanguages();

        if (supportedLanguages.languages.length > 0) {
          text = text.replace(/\n/g, ". ");
          text = text.replace(/\.\./g, ".");
          text = text.replace(/\.\s\./g, ".");

          if (text.startsWith(". ")) {
            text = text.slice(2);
          }

          const speechRate = Number(accessibilitySettings?.contents?.textToSpeechSpeed ?? 1.0);
          const textToSpeechProperties: TTSOptions = {
            text: text ?? "",
            // rate: Number.isNaN(speechRate) ? 1.0 : speechRate,
            rate: 1.0,
            pitch: 1.0,
            volume: 1.0,
            category: "playback",
          };

          if (supportedLanguages.languages.includes("en-GB")) {
            textToSpeechProperties.lang = "en-GB";
          }

          setPlaying(true);

          await TextToSpeech.speak(textToSpeechProperties);

          setPlaying(false);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error?.error !== "interrupted") {
        console.log(error);
      }

      setPlaying(false);
    }
  }, [playing]);

  const stopAudioPlayback = (): void => {
    void TextToSpeech.stop().catch((error) => {
      console.log(error);
    });

    setPlaying(false);
  };

  return { handleTTS: handleAudioPlayback, playing, stopTTS: stopAudioPlayback };
};
